/**
 * Sharing & invitation translation keys.
 *
 * Pattern: The backend (DB → API) returns a `tk` field in error responses
 * that maps directly to i18n keys in public/locales/{lng}/common.json.
 *
 * For API error toasts, use: extractErrorTk(error) → t(tk)
 * For client-side validation hints, use these constants with t().
 *
 * When adding new features, follow this pattern:
 * 1. Define the key constant here
 * 2. Add the translation string to all locale JSON files
 * 3. Add the type to src/types/i18next.d.ts
 * 4. Use the same key string as the `tk` value in the API/DB layer
 */
export const SHARING_ERROR_KEYS = {
  selfInvite: "errors.sharing.selfInvite",
  duplicateInvite: "errors.sharing.duplicateInvite",
  alreadyHasAccess: "errors.sharing.alreadyHasAccess",
  invalidRole: "errors.sharing.invalidRole",
  invitationNotFound: "errors.sharing.invitationNotFound",
  invitationExpired: "errors.sharing.invitationExpired",
  emailMismatch: "errors.sharing.emailMismatch",
  selfInviteAccept: "errors.sharing.selfInviteAccept",
  invalidEmail: "errors.sharing.invalidEmail",
  domainBlocked: "errors.sharing.domainBlocked",
} as const;

/** UI keys for the ShareProjectModal and related components */
export const SHARING_UI_KEYS = {
  dialogTitle: "sharing.dialogTitle",
  dialogDescription: "sharing.dialogDescription",
  emailPlaceholder: "sharing.emailPlaceholder",
  roleEditor: "sharing.roleEditor",
  roleViewer: "sharing.roleViewer",
  roleOwner: "sharing.roleOwner",
  pendingInvitations: "sharing.pendingInvitations",
  peopleWithAccess: "sharing.peopleWithAccess",
  sent: "sharing.sent",
  expired: "sharing.expired",
  expiresInDays: "sharing.expiresInDays",
  expiresInHours: "sharing.expiresInHours",
  invitationSent: "sharing.invitationSent",
  invitationCancelled: "sharing.invitationCancelled",
  accessRemoved: "sharing.accessRemoved",
  manageAccess: "sharing.manageAccess",
  addPeople: "sharing.addPeople",
  addTeams: "sharing.addTeams",
  addPeopleTitle: "sharing.addPeopleTitle",
  addTeamsTitle: "sharing.addTeamsTitle",
  collaborators: "sharing.collaborators",
  searchPeople: "sharing.searchPeople",
  searchTeams: "sharing.searchTeams",
  searchCollaborators: "sharing.searchCollaborators",
  alreadyHasAccess: "sharing.alreadyHasAccess",
  inviteByEmail: "sharing.inviteByEmail",
  teamMemberCount: "sharing.teamMemberCount",
  noMembersFound: "sharing.noMembersFound",
  noTeamsFound: "sharing.noTeamsFound",
  noTeamsAvailable: "sharing.noTeamsAvailable",
  noCollaborators: "sharing.noCollaborators",
  noPendingInvitations: "sharing.noPendingInvitations",
  sharedWithTeam: "sharing.sharedWithTeam",
  invitationSentToTeamOwners: "sharing.invitationSentToTeamOwners",
  you: "sharing.you",
  team: "sharing.team",
  roleUpdated: "sharing.roleUpdated",
  supportAccessBanner: "sharing.supportAccessBanner",
  revoke: "sharing.revoke",
  supportAccessRevoked: "sharing.supportAccessRevoked",
} as const;

/** UI keys for the AcceptInvitationPage and InvitationBanner */
export const INVITATION_KEYS = {
  title: "invitation.title",
  invitedYouTo: "invitation.invitedYouTo",
  invitedYouToJoin: "invitation.invitedYouToJoin",
  role: "invitation.role",
  sentTo: "invitation.sentTo",
  expiredOn: "invitation.expiredOn",
  alreadyUsedOrWithdrawn: "invitation.alreadyUsedOrWithdrawn",
  selfInviteDetail: "invitation.selfInviteDetail",
  emailMismatchDetail: "invitation.emailMismatchDetail",
  acceptButton: "invitation.acceptButton",
  signInToAccept: "invitation.signInToAccept",
  goToProjects: "invitation.goToProjects",
  joinedProject: "invitation.joinedProject",
  joinedTeam: "invitation.joinedTeam",
  declinedInvite: "invitation.declinedInvite",
  declinedTeamInvite: "invitation.declinedTeamInvite",
} as const;

/** Aria-label keys for accessibility in sharing components */
export const SHARING_ARIA_KEYS = {
  sendInvitation: "sharing.aria.sendInvitation",
  cancelInvitation: "sharing.aria.cancelInvitation",
  removeAccess: "sharing.aria.removeAccess",
  revokeSupportAccess: "sharing.aria.revokeSupportAccess",
} as const;
